import type { Client, Phase } from '../../types/domain';
import { phaseJiraHours } from './hours';
import { effJiraKey } from './keys';
import type { DatedPhase, JiraData, MaintenanceLabelHours } from './types';

// Deux phases se chevauchent dans le temps si aucune des deux ne se termine avant que l'autre ne
// commence (dateFin absente = contrat ouvert, toujours en cours).
export function phasesOverlapPeriod(a: DatedPhase, b: DatedPhase): boolean {
  if (a.dateFin && b.dateDebut && a.dateFin < b.dateDebut) return false;
  if (b.dateFin && a.dateDebut && b.dateFin < a.dateDebut) return false;
  return true;
}

// Vrai si une AUTRE phase (de n'importe quel client), sur la même période, résout la même clé Jira
// que `phase` — signe que le projet Jira est partagé entre plusieurs contrats concomitants (ex. une
// maintenance et un forfait de dev sur le même projet, cf. Service-EA/PEPS). Une phase déjà terminée
// avant le début de celle-ci ne compte pas (ex. 3 phases historiques sur le même projet dont 2 déjà
// closes : on s'en fiche, seule la période qui coïncide compte). Si partagé, le tag maint_corrective
// doit arbitrer le quota correctif ; sinon (phase seule sur sa période), comportement historique :
// tout le temps du projet est correctif par défaut, sauf ce qui est taggé non_facturable.
export function jiraKeySharedWithOtherPhase(
  clients: Client[] | null | undefined,
  phase: Phase,
  client: Client | null | undefined,
): boolean {
  const key = effJiraKey(phase, client);
  if (!key) return false;
  return (clients || []).some((c) =>
    (c.phases || []).some(
      (p) => p.id !== phase.id && effJiraKey(p, c) === key && phasesOverlapPeriod(phase, p),
    ),
  );
}

// Temps à retirer du total Jira brut d'une phase NON-maintenance quand une phase de maintenance
// concomitante (même clé Jira, période qui chevauche) existe : sans ça, le temps déjà compté comme
// correctif/évolutif côté maintenance se retrouverait EN PLUS compté ici (double comptage, cf.
// Service-EA/PEPS : Maintenance 2026 + MAJ SDK). Ne retire que ce qui est déjà chargé dans
// maintLabelHours (auto-chargé pour toutes les phases du client ouvert, voir useEffect dédié) —
// tant que ce n'est pas chargé, on ne retire rien plutôt que de risquer une exclusion à tort.
export function maintenanceHoursToExclude(
  clients: Client[] | null | undefined,
  phase: Phase,
  client: Client | null | undefined,
  maintenanceLabelHours: Record<string, MaintenanceLabelHours | undefined>,
): number {
  const key = effJiraKey(phase, client);
  if (!key) return 0;
  const start = phase.dateDebut ? phase.dateDebut.slice(0, 7) : null;
  const end = phase.dateFin ? phase.dateFin.slice(0, 7) : null;
  const inRange = (m: string) => (!start || m >= start) && (!end || m <= end);
  let total = 0;
  for (const c of clients || []) {
    for (const p of c.phases || []) {
      if (p.id === phase.id) continue;
      const isMaint =
        p.facturation === 'maintenance_corrective' || p.facturation === 'maintenance_evolutive';
      if (!isMaint || effJiraKey(p, c) !== key || !phasesOverlapPeriod(phase, p)) continue;
      const lbl = maintenanceLabelHours[p.id];
      if (!lbl || !lbl.loaded) continue;
      for (const [m, h] of Object.entries(lbl.corrByMonth || {})) if (inRange(m)) total += h;
      if (p.facturation === 'maintenance_evolutive') {
        for (const [m, h] of Object.entries(lbl.evolByMonth || {})) if (inRange(m)) total += h;
      }
    }
  }
  return total;
}

const isMaintenancePhase = (p: Phase) =>
  p.facturation === 'maintenance_corrective' || p.facturation === 'maintenance_evolutive';

/* « Consommé Jira » d'une phase, le même sur la fiche projet et sur la Timeline : une phase hors
   maintenance ne recompte pas le temps déjà attribué au correctif/évolutif d'une maintenance
   concomitante du même projet Jira. */
export function phaseNetJiraHours(
  jiraData: JiraData,
  clients: Client[] | null | undefined,
  phase: Phase,
  client: Client | null | undefined,
  maintenanceLabelHours: Record<string, MaintenanceLabelHours | undefined>,
): number {
  const raw = phaseJiraHours(jiraData, effJiraKey(phase, client), phase);
  if (isMaintenancePhase(phase)) return raw;
  return Math.max(
    0,
    raw - maintenanceHoursToExclude(clients, phase, client, maintenanceLabelHours),
  );
}
